// Bank Deposit Modal — records a single real-world client payment and
// allocates it across one or more open balances in one event.
//
// Workflow:
//   1. Bookkeeper picks client → modal pre-loads every open balance for that
//      client across invoices, hours_invoices, and billing_summaries.
//   2. Bookkeeper enters total amount + date + method + bank ref.
//   3. Per-row amount fields let them split the deposit across the listed
//      documents. "Auto-fill in order" populates the first N rows until the
//      deposit is exhausted.
//   4. On save: writes one client_payments row + N payment_allocations rows.
//      Each touched document's derived status flips automatically.
//
// Lives in the toolbar of Client Invoices → All Invoices.

import React, { useState, useMemo, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAppContext } from './AppContext';
import { calcPaidForDocument, calcBillingSummaryAmount, fmtMoney, billingDocDisplayName } from '../utils';

var PAYMENT_METHODS = ['', 'Wire', 'Check', 'ACH', 'Zelle', 'Credit card', 'Other'];

// Tolerant amount parser (2026-07-12): humans type commas and paste
// typographic minus signs — "47,838.74" must read as 47838.74, never as
// an invalid $0 that silently disables Record Deposit. Same normalization
// the Retainers amount field got on 2026-07-08.
function parseAmt(v) {
  var n = parseFloat(String(v == null ? '' : v).replace(/[\u2212\u2013\u2014]/g, '-').replace(/[$,\s]/g, ''));
  return isNaN(n) ? 0 : n;
}

export default function BankDepositModal(props) {
  var ctx = useAppContext();
  var clients = ctx.clients || [];
  var projects = ctx.projects || [];
  var invoices = ctx.invoices || [];
  var proposals = ctx.proposals || [];
  var allItems = ctx.items || [];
  var paymentAllocations = ctx.paymentAllocations || [];
  var recordClientPayment = ctx.recordClientPayment;
  var userEmail = ctx.userEmail || '';

  // If a project is preset (e.g., the invoices page is filtered to one
   // project), derive the client from that project and lock the dropdown.
   // This prevents the bookkeeper from accidentally allocating a deposit
   // to the wrong client's invoices.
  var presetProjectLabel = props.presetProjectLabel || '';
  var presetClientId = (function() {
    if (!presetProjectLabel) return '';
    var p = (projects || []).find(function(pr) { return pr.label === presetProjectLabel; });
    return (p && p.clientId) ? String(p.clientId) : '';
  })();
  var cs = useState(presetClientId);             var clientId = cs[0];      var setClientId = cs[1];

  // If the preset changes (e.g., user changes the invoice tab project filter
  // before opening the modal), sync the local clientId.
  useEffect(function() {
    if (presetClientId && presetClientId !== clientId) setClientId(presetClientId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetClientId, props.open]);
  var ds = useState(new Date().toISOString().slice(0, 10));
  var date = ds[0]; var setDate = ds[1];
  var ts = useState('');                         var totalAmount = ts[0];   var setTotalAmount = ts[1];
  var ms = useState('');                         var method = ms[0];        var setMethod = ms[1];
  var brs = useState('');                        var bankRef = brs[0];      var setBankRef = brs[1];
  var rs = useState('');                         var reference = rs[0];     var setReference = rs[1];
  var ns = useState('');                         var notes = ns[0];         var setNotes = ns[1];
  var aas = useState({});                        var allocAmounts = aas[0]; var setAllocAmounts = aas[1];  // { rowKey → amount-as-string }
  var sas = useState(false);                     var saving = sas[0];       var setSaving = sas[1];
  var ess = useState('');                        var err = ess[0];          var setErr = ess[1];
  var his = useState([]);                        var hoursInvs = his[0];    var setHoursInvs = his[1];
  var bss = useState([]);                        var summaryDocs = bss[0];  var setSummaryDocs = bss[1];

  // Pull hours_invoices and billing_summaries lazily — they aren't on
  // AppContext today and we only need them when this modal is open.
  useEffect(function() {
    if (!props.open) return;
    supabase.from('hours_invoices').select('*').then(function(r) {
      if (!r.error && r.data) setHoursInvs(r.data);
    });
    supabase.from('billing_summaries').select('*').then(function(r) {
      if (!r.error && r.data) setSummaryDocs(r.data);
    });
  }, [props.open]);

  // Find every open balance for the selected client across all three
  // document types. "Open" means amount due > paid sum.
  var openDocs = useMemo(function() {
    if (!clientId) return [];
    var rows = [];

    // Invoices: sent / partially-paid invoices the client has actually
    // received — drafts and voids are excluded — EXCEPT deposit invoices
    // (2026-07-11, the Henrybuilt transferred-deposit case): per-item
    // DEPs are minted at proposal approval with status 'draft' and are
    // payable from birth (the client's approval of the PROPOSAL is the
    // ask; the DEP is internal allocation plumbing). Pre-fix this modal
    // could never take deposit money at all. Draft FINALS stay hidden —
    // a draft FIN hasn't been billed (the May 2026 Hoback incident shape).
    invoices.forEach(function(inv) {
      if (inv.clientId !== clientId) return;
      if (inv.status === 'void') return;
      if (inv.status === 'draft' && (inv.invoiceType || 'deposit') !== 'deposit') return;
      var amt = parseFloat(inv.amountDue) || 0;
      var paid = calcPaidForDocument(paymentAllocations, 'invoice', inv.id);
      var balance = amt - paid;
      if (balance > 0.005) {
        // Names first, refs muted (Cimon 2026-07-12) — same convention as
        // the package payment wizard: the proposal's name is what the
        // bookkeeper recognizes; the ref is cross-reference metadata. For
        // a proposal with several per-item DEPs, append the item name so
        // sibling deposits are tellable apart.
        var friendly = billingDocDisplayName(inv, proposals);
        // Always show the ITEM name on per-item deposit rows (Cimon
        // 2026-07-12 — the item is what she recognizes, not just the
        // proposal), whenever the DEP carries its item link.
        if ((inv.invoiceType || 'deposit') === 'deposit' && inv.itemId) {
          var depItem = allItems.find(function(x) { return x && x.id === inv.itemId; });
          var depItemName = depItem ? (depItem.item || depItem.productName || '') : '';
          if (depItemName) friendly += ' · ' + depItemName;
        }
        rows.push({
          key:           'invoice:' + inv.id,
          documentType:  'invoice',
          documentId:    inv.id,
          label:         friendly,
          ref:           inv.invoiceRef || '',
          subtype:       (inv.invoiceType || 'deposit'),
          amountDue:     amt,
          paid:          paid,
          balance:       balance,
          dateText:      (inv.createdAt || '').slice(0, 10),
        });
      }
    });

    // Hours invoices: matched by client_name → clients[].name → clientId,
    // OR via project_label → projects[].clientId.
    var clientName = (clients.find(function(c) { return c.id === clientId; }) || {}).name || '';
    hoursInvs.forEach(function(hi) {
      var matchByName = hi.client_name === clientName;
      var proj = projects.find(function(p) { return p.label === hi.project_label; });
      var matchByProj = proj && proj.clientId === clientId;
      if (!matchByName && !matchByProj) return;
      var amt = parseFloat(hi.total_amount) || 0;
      var paid = calcPaidForDocument(paymentAllocations, 'hours_invoice', hi.invoice_number);
      var balance = amt - paid;
      if (balance > 0.005 && hi.status !== 'Draft') {
        rows.push({
          key:           'hours_invoice:' + hi.invoice_number,
          documentType:  'hours_invoice',
          documentId:    hi.invoice_number,
          label:         hi.invoice_number,
          subtype:       'designer hours',
          amountDue:     amt,
          paid:          paid,
          balance:       balance,
          dateText:      hi.invoice_date || '',
        });
      }
    });

    // Billing summaries: matched via project_label → projects[].clientId.
    // billing_summaries has no amount_due column — the asked amount is
    // computed from design_amount + additional_services + expenses (the
    // same bsSubtotal BillingSummarySection uses). For retainer projects
    // the asked amount also includes the sum of reimbursable invoices
    // tagged onto this summary (since 2026-05-11 — the cover folds them
    // into the retainer-eligible section).
    summaryDocs.forEach(function(bs) {
      var proj = projects.find(function(p) { return p.label === bs.project_label; });
      if (!proj || proj.clientId !== clientId) return;
      var amt = calcBillingSummaryAmount(bs, invoices, !!proj.hasRetainer);
      var paid = calcPaidForDocument(paymentAllocations, 'billing_summary', bs.id);
      var balance = amt - paid;
      if (balance > 0.005 && bs.status !== 'draft') {
        rows.push({
          key:           'billing_summary:' + bs.id,
          documentType:  'billing_summary',
          documentId:    bs.id,
          label:         (bs.year || '') + '-' + String(bs.month || '').padStart(2, '0') + ' summary',
          subtype:       'monthly summary',
          amountDue:     amt,
          paid:          paid,
          balance:       balance,
          dateText:      bs.created_at || '',
        });
      }
    });

    return rows.sort(function(a, b) { return (a.dateText || '').localeCompare(b.dateText || ''); });
  }, [clientId, invoices, paymentAllocations, hoursInvs, summaryDocs, clients, projects]);

  // Auto-fill the deposit across rows in order, until exhausted.
  function autoFillInOrder() {
    var remaining = parseAmt(totalAmount);
    if (remaining <= 0) return;
    var next = {};
    openDocs.forEach(function(row) {
      if (remaining <= 0.005) { next[row.key] = ''; return; }
      var take = Math.min(row.balance, remaining);
      next[row.key] = take.toFixed(2);
      remaining -= take;
    });
    setAllocAmounts(next);
  }

  function clearAllocations() {
    setAllocAmounts({});
  }

  // Sum of all allocation inputs, as a number.
  var allocatedTotal = useMemo(function() {
    return Object.keys(allocAmounts).reduce(function(s, k) {
      return s + parseAmt(allocAmounts[k]);
    }, 0);
  }, [allocAmounts]);

  var depositAmount = parseAmt(totalAmount);
  var unallocated = depositAmount - allocatedTotal;
  var canSave = clientId && depositAmount > 0 && allocatedTotal > 0 && Math.abs(unallocated) < 0.01;

  async function submit() {
    if (saving) return;  // double-click guard — prevents two payments on rapid double-fire
    setErr('');
    if (!clientId) { setErr('Pick a client.'); return; }
    if (depositAmount <= 0) { setErr('Enter a deposit amount.'); return; }
    if (allocatedTotal <= 0) { setErr('Allocate to at least one document.'); return; }
    if (Math.abs(unallocated) >= 0.01) {
      setErr('Allocations must sum to the deposit amount. Unallocated: ' + fmtMoney(unallocated));
      return;
    }
    setSaving(true);

    var allocations = openDocs
      .filter(function(row) { return parseAmt(allocAmounts[row.key]) > 0; })
      .map(function(row) {
        return {
          documentType: row.documentType,
          documentId:   String(row.documentId),
          amount:       parseAmt(allocAmounts[row.key]),
        };
      });

    var clientObj = clients.find(function(c) { return c.id === clientId; });
    var projectLabel = '';
    if (allocations.length === 1) {
      var first = openDocs.find(function(r) { return r.key === Object.keys(allocAmounts).find(function(k) { return parseAmt(allocAmounts[k]) > 0; }); });
      if (first) {
        if (first.documentType === 'invoice') {
          var inv = invoices.find(function(i) { return i.id === first.documentId; });
          projectLabel = (inv && (inv.project || inv.project_label)) || '';
        }
      }
    }

    var payment = {
      clientId:       clientId,
      projectLabel:   projectLabel,
      paymentDate:    date,
      amountTotal:    depositAmount,
      method:         method,
      bankReference:  bankRef.trim(),
      reference:      reference.trim() || ('Deposit from ' + (clientObj ? clientObj.name : '')),
      notes:          notes.trim(),
      recordedBy:     userEmail,
    };

    var res = await recordClientPayment(payment, allocations);

    if (!res || !res.ok) {
      setSaving(false);
      setErr('Save failed: ' + (res && res.error ? (res.error.message || 'unknown') : 'unknown'));
      return;
    }

    // Wait for caller's side effects (status flips, item cascades, retainer
    // auto-draws) before closing so any failures surface to the user.
    if (props.onSaved) {
      try { await props.onSaved(res.payment, res.allocations); }
      catch (e) { console.error('BankDepositModal onSaved failed:', e); }
    }
    setSaving(false);
    props.onClose();
  }

  if (!props.open) return null;

  var labelStyle = { fontSize: 9, fontWeight: 500, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888880', marginBottom: 4 };
  var inputStyle = { width: '100%', padding: '8px 10px', border: '0.5px solid #D8D7D2', borderRadius: 3, fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 720, maxHeight: '92vh', overflowY: 'auto', background: '#FFFFFF', borderRadius: 6, boxShadow: '0 8px 40px rgba(0,0,0,0.3)', padding: 22 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 500, color: '#111111' }}>Record Bank Deposit</div>
          <button onClick={props.onClose}
            style={{ padding: '4px 10px', fontSize: 11, color: '#1a1a1a', background: '#FFFFFF', border: '0.5px solid #D8D7D2', borderRadius: 4, cursor: 'pointer' }}>
            Close
          </button>
        </div>

        {/* Client + total + meta */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
          <div>
            <div style={labelStyle}>Client</div>
            {presetClientId ? (
              // Locked when opened from a project-filtered invoice list -- the
              // client is derived from the project to prevent cross-client
              // mis-allocation.
              <div style={Object.assign({}, inputStyle, { background: '#F7F7F5', color: '#111', display: 'flex', alignItems: 'center', justifyContent: 'space-between' })}>
                <span>{(clients.find(function(c){return String(c.id) === String(presetClientId);}) || {}).name || '—'}</span>
                <span style={{ fontSize: 10, color: '#888', marginLeft: 8 }}>locked · {presetProjectLabel}</span>
              </div>
            ) : (
              <select value={clientId}
                onChange={function(e) { setClientId(e.target.value); setAllocAmounts({}); }}
                style={Object.assign({}, inputStyle, { background: '#fff' })}>
                <option value="">— Pick a client —</option>
                {clients.map(function(c) {
                  return <option key={c.id} value={c.id}>{c.name}</option>;
                })}
              </select>
            )}
          </div>
          <div>
            <div style={labelStyle}>Date received</div>
            <input type="date" value={date} onChange={function(e) { setDate(e.target.value); }} style={inputStyle} />
          </div>
          <div>
            <div style={labelStyle}>Total deposit</div>
            <input type="text" inputMode="decimal" autoCorrect="off" autoComplete="off" spellCheck={false} placeholder="0.00" value={totalAmount}
              onChange={function(e) { setTotalAmount(e.target.value); }} style={inputStyle} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.4fr', gap: 10, marginBottom: 14 }}>
          <div>
            <div style={labelStyle}>Method</div>
            <select value={method} onChange={function(e) { setMethod(e.target.value); }}
              style={Object.assign({}, inputStyle, { background: '#fff' })}>
              {PAYMENT_METHODS.map(function(m) {
                return <option key={m} value={m}>{m || '— Pick a method —'}</option>;
              })}
            </select>
          </div>
          <div>
            <div style={labelStyle}>Bank reference</div>
            <input type="text" placeholder="Wire conf # / check #" value={bankRef}
              onChange={function(e) { setBankRef(e.target.value); }} style={inputStyle} />
          </div>
          <div>
            <div style={labelStyle}>Reference (general)</div>
            <input type="text" placeholder="e.g. April retainer wire" value={reference}
              onChange={function(e) { setReference(e.target.value); }} style={inputStyle} />
          </div>
        </div>

        {/* Open balances list */}
        {clientId && (
          <div style={{ borderTop: '0.5px solid #D8D7D2', paddingTop: 14, marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 500, color: '#111111' }}>
                Allocate to open balances {openDocs.length > 0 ? '(' + openDocs.length + ')' : ''}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={autoFillInOrder} disabled={!depositAmount}
                  style={{ padding: '4px 10px', fontSize: 11, color: '#1a1a1a', background: '#FFFFFF', border: '0.5px solid #D8D7D2', borderRadius: 4, cursor: depositAmount ? 'pointer' : 'not-allowed', opacity: depositAmount ? 1 : 0.5 }}>
                  Auto-fill in order
                </button>
                <button onClick={clearAllocations}
                  style={{ padding: '4px 10px', fontSize: 11, color: '#1a1a1a', background: '#FFFFFF', border: '0.5px solid #D8D7D2', borderRadius: 4, cursor: 'pointer' }}>
                  Clear
                </button>
              </div>
            </div>

            {openDocs.length === 0 ? (
              <div style={{ padding: '14px 12px', background: '#F7F7F5', borderRadius: 4, fontSize: 11, color: '#888880' }}>
                No open balances for this client.
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                  <tr style={{ borderBottom: '0.5px solid #D8D7D2' }}>
                    <th style={{ textAlign: 'left',  padding: '6px 8px', fontWeight: 500, color: '#888880', textTransform: 'uppercase', fontSize: 9, letterSpacing: '0.07em' }}>Document</th>
                    <th style={{ textAlign: 'left',  padding: '6px 8px', fontWeight: 500, color: '#888880', textTransform: 'uppercase', fontSize: 9, letterSpacing: '0.07em' }}>Type</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 500, color: '#888880', textTransform: 'uppercase', fontSize: 9, letterSpacing: '0.07em' }}>Balance</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 500, color: '#888880', textTransform: 'uppercase', fontSize: 9, letterSpacing: '0.07em', width: 130 }}>Apply</th>
                  </tr>
                </thead>
                <tbody>
                  {openDocs.map(function(row) {
                    return (
                      <tr key={row.key} style={{ borderBottom: '0.5px dotted #EFEFEC' }}>
                        <td style={{ padding: '8px', color: '#111111' }}>
                          {row.label}
                          {row.ref && (
                            <div style={{ fontSize: 10, color: '#888880', fontFamily: 'monospace', marginTop: 1 }}>{row.ref}</div>
                          )}
                        </td>
                        <td style={{ padding: '8px', color: '#555552' }}>{row.subtype}</td>
                        <td style={{ padding: '8px', textAlign: 'right', color: '#111111', fontWeight: 500 }}>{fmtMoney(row.balance)}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                          <input type="text" inputMode="decimal" autoCorrect="off" autoComplete="off" spellCheck={false} placeholder="0.00"
                            value={allocAmounts[row.key] || ''}
                            onChange={function(e) {
                              var v = e.target.value;
                              setAllocAmounts(function(prev) {
                                var next = Object.assign({}, prev);
                                if (v === '') delete next[row.key]; else next[row.key] = v;
                                return next;
                              });
                            }}
                            style={Object.assign({}, inputStyle, { textAlign: 'right', padding: '5px 8px' })} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '0.5px solid #111111' }}>
                    <td colSpan={2} style={{ padding: '8px', textAlign: 'right', color: '#888880', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Total allocated</td>
                    <td style={{ padding: '8px', textAlign: 'right', color: '#111111', fontWeight: 500 }}>{fmtMoney(allocatedTotal)}</td>
                    <td style={{ padding: '8px' }}></td>
                  </tr>
                  <tr>
                    <td colSpan={2} style={{ padding: '8px', textAlign: 'right', color: '#888880', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Unallocated</td>
                    <td style={{ padding: '8px', textAlign: 'right', color: Math.abs(unallocated) < 0.01 ? '#1A5C38' : '#C8322A', fontWeight: 500 }}>
                      {fmtMoney(unallocated)}
                    </td>
                    <td style={{ padding: '8px' }}></td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        )}

        <div style={{ marginBottom: 14 }}>
          <div style={labelStyle}>Notes (optional)</div>
          <textarea rows={2} value={notes} onChange={function(e) { setNotes(e.target.value); }}
            style={Object.assign({}, inputStyle, { resize: 'vertical' })} />
        </div>

        {err && (
          <div style={{ marginBottom: 12, padding: '8px 10px', background: '#FDECEA', border: '0.5px solid #F2A39C', borderRadius: 3, fontSize: 11, color: '#C8322A' }}>{err}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={props.onClose}
            style={{ padding: '6px 12px', border: '0.5px solid #D8D7D2', borderRadius: 4, background: '#FFFFFF', color: '#111111', fontSize: 11, fontFamily: 'inherit', cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={submit} disabled={!canSave || saving}
            style={{ padding: '6px 12px', border: '0.5px solid #1A5C38', borderRadius: 4, background: canSave && !saving ? '#1A5C38' : '#888880', color: '#FFFFFF', fontSize: 11, fontFamily: 'inherit', cursor: canSave && !saving ? 'pointer' : 'not-allowed' }}>
            {saving ? 'Saving…' : 'Record Deposit'}
          </button>
        </div>
      </div>
    </div>
  );
}
